/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import Text from "../ui/Text";
import React, { useState } from "react";
import Anchor from "../ui/Anchor";
import Form from "../ui/form/Form";
import FormFieldsWrapper from "../ui/form/FormFieldsWrapper";
import FormGroup from "../ui/form/FormGroup";
import EmailField from "../ui/form/EmailField";
import FormError from "../ui/form/FormError";
import Button from "../ui/Button";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { useAuth } from "../../hooks/useAuth";
import Icon from "../ui/Icon";
import { useToast } from "../ui/Toast";
import { extractErrorMessage } from "../../utils/api/apiErrors";

const StyledFooterText = styled((props) => <Text {...props} />)`
    text-align: center;
`;

const StyledFormWrapper = styled.div`
    display: grid;
    gap: 24px;
`;

const StyledIcon = styled((props) => <Icon.EnvelopeEmailIcon {...props} />)`
    margin: 0 auto;
    color: rgb(${({ theme }) => theme.colors.textSecondary.rgb});
`;

const StyledSentLinkSection = styled.div`
    display: grid;
    gap: 16px;
`;

const StyledSentLinkInfoSection = styled.div`
    display: grid;
    gap: 8px;
`;

const StyledSentLinkFooter = styled.div`
    display: flex;
    gap: 32px;
    justify-content: center;
    text-align: center;
`;

const ClientForgotPasswordView = ({ onLoginClick }) => {
  const [sentRecoveryLink, setSentRecoveryLink] = useState(false);
  const [email, setEmail] = useState("");
  const { remindPassword, isRemindPasswordPending } = useAuth();
  const { toast } = useToast();
  const {
    handleSubmit,
    setError,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
    },
  });

  const handleRemindPassword = async (data) => {
    const { email: formEmail } = data;
    try {
      await remindPassword({ email: formEmail });
      toast({
        title: "Success",
        description: "Recovery link was sent to your email",
        variant: "success",
      })
      setSentRecoveryLink(true);
      setEmail(formEmail);
    } catch (error) {
      const message = extractErrorMessage(error, "Something went wrong");
      toast({
        title: "Error",
        description: message,
        variant: "error",
      })
      setError("root", { message });
    }
  }


  return (
    <>
      {sentRecoveryLink && <>
        <StyledSentLinkSection>
          <StyledIcon color="primary" height={124} width={124} />
          <StyledSentLinkInfoSection>
            <Text color="dark">We sent a recovery link to you at</Text>
            <Text color="dark" weight="bold">{email}</Text>
            <Text color="dark" size="small">If you don't see it after a few minutes, check your spam folder.</Text>
          </StyledSentLinkInfoSection>
        </StyledSentLinkSection>
        <StyledSentLinkFooter>
          <StyledFooterText size="small">
            <Anchor onClick={onLoginClick}>Return to Sign In</Anchor>
          </StyledFooterText>
          <StyledFooterText size="small">
            <Anchor onClick={handleSubmit(handleRemindPassword)}>Resend recovery link</Anchor>
          </StyledFooterText>
        </StyledSentLinkFooter>
      </>
      }

      {!sentRecoveryLink &&
        <StyledFormWrapper>
          <Form onSubmit={handleSubmit(handleRemindPassword)}>
            <FormFieldsWrapper>
              <FormGroup>
                <EmailField control={control} name="email" label="We will send recovery link to"
                  placeholder="enter_email"
                  required
                  hideRequiredIndicator />
                {errors.email && <FormError name="email">{errors.email.message}</FormError>}
              </FormGroup>
            </FormFieldsWrapper>
            <Button variant="primary" title="Send code" loading={isRemindPasswordPending} fullWidth />
            {errors.root && <FormError>{errors.root.message}</FormError>}
          </Form>
          <StyledFooterText size="small">
            <Anchor onClick={onLoginClick}>Return to Sign In</Anchor>
          </StyledFooterText>
        </StyledFormWrapper>
      }
    </>
  );
}

export default ClientForgotPasswordView;